import type { Capability } from '../config/navigation';

/**
 * Help articles behind the Support button.
 *
 * Each article is a markdown file in ./articles with a small frontmatter
 * block. They live in the repo rather than the database so they are
 * reviewed with the code they describe and ship with it - a page that
 * changes and its article can land in the same commit.
 *
 *   ---
 *   title: Orders History
 *   category: Orders & Shipping
 *   summary: One sentence shown in lists and search results.
 *   route: /external-orders          (optional: the page it is about)
 *   capability: shipping.view        (optional: hidden from users without it)
 *   order: 10                        (optional: position inside its category)
 *   ---
 */
export interface HelpArticle {
  slug: string;
  title: string;
  category: string;
  summary: string;
  route?: string;
  capability?: Capability;
  order: number;
  body: string;
}

/** Category order in the drawer; anything not listed sorts after these. */
export const HELP_CATEGORIES = [
  'Getting Started',
  'Orders & Shipping',
  'Tracking',
  'Catalog',
  'Purchasing & Packaging',
  'Reports',
  'Tickets',
  'Logs',
  'Settings',
  'Troubleshooting',
];

const files = import.meta.glob('./articles/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const parse = (path: string, raw: string): HelpArticle | null => {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
  if (!match) return null;
  const meta: Record<string, string> = {};
  for (const line of match[1].split(/\r?\n/)) {
    const i = line.indexOf(':');
    if (i > 0) meta[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  if (!meta.title || !meta.category) return null;
  return {
    slug: path.replace(/^.*\//, '').replace(/\.md$/, ''),
    title: meta.title,
    category: meta.category,
    summary: meta.summary || '',
    route: meta.route || undefined,
    capability: (meta.capability as Capability) || undefined,
    order: Number(meta.order) || 100,
    body: match[2].trim(),
  };
};

const categoryRank = (category: string) => {
  const i = HELP_CATEGORIES.indexOf(category);
  return i === -1 ? HELP_CATEGORIES.length : i;
};

export const HELP_ARTICLES: HelpArticle[] = Object.entries(files)
  .map(([path, raw]) => parse(path, raw))
  .filter((a): a is HelpArticle => a !== null)
  .sort(
    (a, b) =>
      categoryRank(a.category) - categoryRank(b.category) ||
      a.category.localeCompare(b.category) ||
      a.order - b.order ||
      a.title.localeCompare(b.title),
  );

/** The route's own article first, then any whose route is a parent of it. */
export const articlesForRoute = (articles: HelpArticle[], pathname: string) =>
  articles
    .filter((a) => a.route && (pathname === a.route || pathname.startsWith(`${a.route}/`)))
    .sort((a, b) => (b.route?.length ?? 0) - (a.route?.length ?? 0));

/**
 * Every word of the query must appear somewhere in the article; title hits
 * rank above summary hits, which rank above body hits.
 */
export const searchArticles = (articles: HelpArticle[], query: string) => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  return articles
    .map((a) => {
      const title = a.title.toLowerCase();
      const summary = a.summary.toLowerCase();
      const body = a.body.toLowerCase();
      let score = 0;
      for (const w of words) {
        if (title.includes(w)) score += 10;
        else if (summary.includes(w)) score += 4;
        else if (body.includes(w)) score += 1;
        else return null;
      }
      return { a, score };
    })
    .filter((r): r is { a: HelpArticle; score: number } => r !== null)
    .sort((x, y) => y.score - x.score)
    .map((r) => r.a);
};
